import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Footer: React.FC = () => {
    const currentYear = new Date().getFullYear();

    const footerSections = [
        {
            title: 'Company',
            links: [
                { name: 'About Us', href: '/about' },
                { name: 'Careers', href: '/careers' },
                { name: 'Press', href: '/press' },
                { name: 'Blog', href: '/blog' }
            ]
        },
        {
            title: 'Support',
            links: [
                { name: 'Help Center', href: '/help' },
                { name: 'Contact Us', href: '/contact' },
                { name: 'Safety Center', href: '/safety' },
                { name: 'Community Guidelines', href: '/guidelines' }
            ]
        },
        {
            title: 'Legal',
            links: [
                { name: 'Terms of Service', href: '/terms' },
                { name: 'Privacy Policy', href: '/privacy' },
                { name: 'Cookie Policy', href: '/cookies' },
                { name: 'GDPR', href: '/gdpr' }
            ]
        },
        {
            title: 'Services',
            links: [
                { name: 'Hotels', href: '/hotels' },
                { name: 'Flights', href: '/flights' },
                { name: 'Car Rentals', href: '/cars' },
                { name: 'Travel Insurance', href: '/insurance' }
            ]
        }
    ];

    const socialLinks = [
        { name: 'Facebook', icon: '📘', href: 'https://facebook.com' },
        { name: 'Twitter', icon: '🐦', href: 'https://twitter.com' },
        { name: 'Instagram', icon: '📷', href: 'https://instagram.com' },
        { name: 'LinkedIn', icon: '💼', href: 'https://linkedin.com' },
        { name: 'YouTube', icon: '📺', href: 'https://youtube.com' }
    ];

    return (
        <footer className="footer">
            <div className="container">
                {/* Main Footer Content */}
                <div className="footer-content">
                    {/* Company Info */}
                    <motion.div 
                        className="footer-section company-info"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <div className="footer-logo">
                            <div className="logo-icon">✈️</div>
                            <div className="logo-text">
                                <h3>MB Tourism</h3>
                                <p>Discover Your Adventure</p>
                            </div>
                        </div>
                        <p className="company-description">
                            Your trusted partner for unforgettable travel experiences. 
                            We make travel planning effortless and enjoyable.
                        </p>
                        
                        {/* Social Links */}
                        <div className="social-links">
                            {socialLinks.map((social, index) => (
                                <motion.a
                                    key={social.name}
                                    href={social.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="social-link"
                                    whileHover={{ scale: 1.1, y: -2 }}
                                    whileTap={{ scale: 0.95 }}
                                    initial={{ opacity: 0, scale: 0 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.1, duration: 0.3 }}
                                    viewport={{ once: true }}
                                >
                                    <span className="social-icon">{social.icon}</span>
                                    <span className="social-name">{social.name}</span>
                                </motion.a>
                            ))}
                        </div>
                    </motion.div>

                    {/* Footer Links */}
                    <div className="footer-links">
                        {footerSections.map((section, sectionIndex) => (
                            <motion.div 
                                key={section.title}
                                className="footer-section"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: sectionIndex * 0.1, duration: 0.6 }}
                                viewport={{ once: true }}
                            >
                                <h4 className="footer-section-title">{section.title}</h4>
                                <ul className="footer-links-list">
                                    {section.links.map((link, linkIndex) => (
                                        <motion.li 
                                            key={link.name}
                                            initial={{ opacity: 0, x: -10 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            transition={{ delay: (sectionIndex * 0.1) + (linkIndex * 0.05), duration: 0.3 }}
                                            viewport={{ once: true }}
                                        >
                                            <Link to={link.href} className="footer-link">
                                                {link.name}
                                            </Link>
                                        </motion.li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Newsletter Section */}
                <motion.div 
                    className="newsletter-section"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    viewport={{ once: true }}
                >
                    <div className="newsletter-content">
                        <h3>Stay Updated</h3>
                        <p>Get the latest travel deals and inspiration delivered to your inbox.</p>
                        <div className="newsletter-form">
                            <input 
                                type="email" 
                                placeholder="Enter your email address"
                                className="newsletter-input"
                            />
                            <motion.button 
                                className="btn btn-primary newsletter-btn"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                Subscribe
                            </motion.button>
                        </div>
                    </div>
                </motion.div>

                {/* Bottom Footer */}
                <motion.div 
                    className="footer-bottom"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    viewport={{ once: true }}
                >
                    <div className="footer-bottom-content">
                        <p className="copyright">
                            © {currentYear} MB Tourism. All rights reserved.
                        </p>
                        <div className="footer-bottom-links">
                            <Link to="/terms" className="footer-bottom-link">Terms</Link>
                            <Link to="/privacy" className="footer-bottom-link">Privacy</Link>
                            <Link to="/cookies" className="footer-bottom-link">Cookies</Link>
                        </div>
                    </div>
                </motion.div>
            </div>
        </footer>
    );
};

export default Footer;
